// src/hooks/useInstructorSchedule.js
//
// Scheduling pilot (Pass 42) — REAL-TIME instructor schedule.
//
// Replaces the old one-shot, whole-org, hardcoded-date-window load with a live
// Firestore listener scoped to this instructor. An ops change to a booking
// reaches the instructor's open screen within seconds (no reload / no tab
// switch). Cancelled lessons are surfaced (not silently dropped) and a
// `lastUpdatedAt` heartbeat + per-lesson "recently changed" flag are exposed.

import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeBooking } from '../utils/booking';
// Pass 83B §8 — the instructor day/time now follows the SELECTED ORGANIZATION's operating timezone
// (generic zoned* helpers), exactly like Pass 82C.2 tenant-neutralized operations. The caller passes the
// org's resolved IANA zone (`timeZone`); SAS resolves to Asia/Tokyo so it stays visually identical, while a
// non-JST org sees its own day. When no zone is supplied we fall back to the SAME LEGACY_OPS_TIMEZONE the
// rest of the app uses as its final fallback (never the browser zone, never a silent generic default).
import { zonedDateKey, zonedStartOfDay, zonedShiftDays, wallClockToInstant, LEGACY_OPS_TIMEZONE } from '../utils/opsTime';
// Pass 57A — schedule visibility policy enforcement (org/school instructors see only their published
// horizon; collective self-managed sees all). App-layer enforcement + honest "published through" UX;
// firestore.rules is the security layer.
import { isBookingVisibleToInstructorNow, publishedThroughDate } from '../utils/scheduleVisibility';

// ── Instructor read-path contract (single source of truth) ────────────────────
// A logged-in instructor's "My Schedule" is the UNION of bookings where the
// logged-in Firebase Auth UID matches EITHER field below. Canonical bookings set
// `instructorId = seed_instructor_*` (the org member) and `instructorAuthUserId =`
// the member's linked Auth UID; the legacy field covers old rows where
// instructorId happened to equal the Auth UID. Every producer of instructor
// bookings — the ops UI, booking cases, AND the Booking Data Master sheet sync —
// must populate these SAME fields so there is ONE schedule per human, never a
// second "spreadsheet schedule". The sheet sync derives instructorAuthUserId
// solely from the assigned member's Auth link.
export const INSTRUCTOR_SCHEDULE_MATCH_FIELDS = ['instructorAuthUserId', 'instructorId'];

// Pure predicate mirroring the two listener queries below. Exported so tests can
// assert the read path against the exact fields the sync writes (no divergence).
export function bookingVisibleToInstructorAuth(booking, authUid) {
  if (!booking || !authUid) return false;
  return INSTRUCTOR_SCHEDULE_MATCH_FIELDS.some((f) => booking[f] === authUid);
}

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Zoned day bucket. Kept for the legacy `dateKey` export used by callers/tests; defaults to the app's
// final-fallback zone (Asia/Tokyo) so existing SAS behaviour is unchanged. Live callers group by the
// org zone via the hook's `timeZone` option (see below).
export function dateKey(value, timeZone = LEGACY_OPS_TIMEZONE) {
  return zonedDateKey(value, timeZone);
}

// A lesson counts as "recently changed" if it was edited/reassigned within the
// window (default 18h) — this drives the "Changed" badge on the instructor view.
export function isRecentlyChanged(booking, withinMs = 18 * 60 * 60 * 1000, now = Date.now()) {
  const updated = toDate(booking.updatedAt)?.getTime() || 0;
  const reassigned = toDate(booking.reassignedAt)?.getTime() || 0;
  const created = toDate(booking.createdAt)?.getTime() || 0;
  const mostRecentEdit = Math.max(updated, reassigned);
  // Ignore the initial create (created ~= updated) so brand-new imports don't all
  // show as "changed".
  if (mostRecentEdit && created && Math.abs(mostRecentEdit - created) < 1000) return false;
  return mostRecentEdit > 0 && now - mostRecentEdit <= withinMs;
}

// Pass 83B §11 — only a genuinely RESTRICTED (rolling-horizon) policy switches the listener to the
// security-queryable `instructorVisibleFromMs <= now` range query. A full-season / self-managed / absent
// policy uses the broad org-scoped query so a booking that has not been backfilled with
// `instructorVisibleFromMs` (the publishing service is not deployed — scheduleVisibility.js) is NEVER
// dropped from the instructor's view. The Firestore rule still enforces per-booking visibility server-side.
export function shouldEnforceVisibilityQuery(policy) {
  return !!(policy && policy.applicable && policy.mode === 'rolling' && policy.horizonDays
    && policy.enforceVisibilityQuery !== false);
}

// Pass 83B.2 §14 — the scope identity a snapshot was produced under. Data is exposed ONLY when the snapshot's
// scopeKey matches the CURRENTLY REQUESTED scope, so a prior org's records are suppressed SYNCHRONOUSLY at
// render (before the re-subscribe effect runs), not merely cleared afterwards.
export function scheduleScopeKey(instructorId, organizationId) {
  return instructorId && organizationId ? `${instructorId}::${organizationId}` : '';
}

export function useInstructorSchedule(instructorId, { organizationId = '', timeZone = '', daysBefore = 1, daysAfter = 14, visibilityPolicy = null } = {}) {
  // Scope-keyed snapshot: { scopeKey, items, lastUpdatedAt, error, loaded }. The stored scopeKey records the
  // uid+org under which `items` were produced.
  const [snap, setSnap] = useState({ scopeKey: '', items: [], lastUpdatedAt: null, error: null, loaded: false });

  // Primitive query-shaping keys, so a fresh policy object identity does not needlessly re-subscribe
  // (the policy is applied in the derived useMemo). Org scoping (§4) is a query-level filter.
  const enforce = shouldEnforceVisibilityQuery(visibilityPolicy);
  // Pass 83B.1 §11/§12 — FAIL CLOSED: the live schedule requires BOTH an Auth identity AND a selected
  // organization. Without an org we do NOT fall back to an identity-only cross-org query (that could blend
  // tenants); we return empty. `scopeMissing` lets the surface show an honest "org context" state (§14).
  const scoped = !!(instructorId && organizationId);
  const requestedScopeKey = scheduleScopeKey(instructorId, organizationId);

  useEffect(() => {
    // Pass 83B.1 §18 / 83B.2 §14 — stamp state with the NEW requested scope immediately (empty items), so a
    // previous org's bookings are never exposed under the new scope (the render binding below also enforces
    // this synchronously).
    setSnap({ scopeKey: requestedScopeKey, items: [], lastUpdatedAt: null, error: null, loaded: !scoped });
    if (!scoped) {
      return undefined; // no org (or no identity) → fail closed, no listener, no cross-org read.
    }
    // Pass 48 §28: `instructorId` here is the logged-in Firebase Auth UID. Canonical
    // bookings store instructorId = seed_instructor_* and a linked instructorAuthUserId.
    // Subscribe to BOTH (new auth-linked + legacy where instructorId happened to equal the
    // uid) and merge/dedup by doc id, so the instructor sees exactly their own bookings.
    const authUid = instructorId;
    const merged = new Map(); // docId -> normalized booking
    const emit = () => setSnap({ scopeKey: requestedScopeKey, items: [...merged.values()], lastUpdatedAt: new Date(), error: null, loaded: true });
    const onErr = (err) => { console.error('Instructor schedule listener error:', err); setSnap((s) => ({ ...s, scopeKey: requestedScopeKey, error: err, loaded: true })); };
    const [authField, legacyField] = INSTRUCTOR_SCHEDULE_MATCH_FIELDS;
    // Pass 83B §4 — the schedule is scoped to Auth identity + SELECTED ORGANIZATION, so a single Auth uid
    // linked to more than one org never receives a blended schedule. The two equality filters
    // (instructor field + organizationId) are served by single-field indexes (zigzag merge) — no composite
    // index needed. Cross-org filtering is done in the QUERY, never client-side (spec §4).
    // Pass 57D2 (item 5) / §11: SECURITY-QUERYABLE range added ONLY for a restricted rolling policy.
    const nowMs = Date.now();
    const build = (field) => {
      const conds = [where(field, '==', authUid), where('organizationId', '==', organizationId)];
      if (enforce) conds.push(where('instructorVisibleFromMs', '<=', nowMs));
      return query(collection(db, 'bookings'), ...conds);
    };
    const qNew = build(authField);
    const qLegacy = build(legacyField);
    const applyOnce = (snap, tag) => {
      // remove prior entries from this listener, then add current
      for (const [id, v] of merged) if (v.__src === tag) merged.delete(id);
      snap.docs.forEach((d) => merged.set(d.id, { ...normalizeBooking({ id: d.id, ...d.data() }), __src: tag }));
      emit();
    };
    const unsubNew = onSnapshot(qNew, (s) => applyOnce(s, 'auth'), onErr);
    const unsubLegacy = onSnapshot(qLegacy, (s) => applyOnce(s, 'legacy'), onErr);
    return () => { unsubNew(); unsubLegacy(); };
    // The listeners depend on the instructor identity + org scope + enforce shape; the visibility POLICY
    // details are applied in the derived useMemo below (which lists it), not by re-subscribing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId, organizationId, scoped, enforce]);

  // Pass 83B.2 §14/§15 — SYNCHRONOUS scope binding. Expose the snapshot ONLY when it was produced for the
  // CURRENTLY requested scope; otherwise (initial render, or the render right after an org switch and before
  // the effect re-subscribes) treat it as empty + loading, so stale-scope data can never render.
  const matched = scoped && snap.scopeKey === requestedScopeKey;
  const lastUpdatedAt = matched ? snap.lastUpdatedAt : null;
  const error = matched ? snap.error : null;
  const loading = scoped ? !(matched && snap.loaded) : false;

  const derived = useMemo(() => {
    // Scope-bound raw bookings (empty unless the snapshot matches the current requested scope).
    const rawBookings = matched ? snap.items : [];
    // Org operating-day window: [today − daysBefore, today + daysAfter], all in the org's IANA zone.
    const tz = timeZone || LEGACY_OPS_TIMEZONE;
    const now = new Date();
    const todayStart = zonedStartOfDay(now, tz).getTime();
    const windowStart = zonedShiftDays(now, -daysBefore, tz).getTime();
    const windowEnd = zonedShiftDays(now, daysAfter + 1, tz).getTime();
    const weekEnd = zonedShiftDays(now, 7, tz).getTime();

    // Pass 57A: enforce the instructor's publishing horizon FIRST. A booking outside the published
    // window (and not published-early) is not exposed at all. When no policy is supplied the behaviour
    // is unchanged (full visibility). Collective self-managed policies are `applicable:false` → all shown.
    const published = visibilityPolicy
      ? rawBookings.filter((b) => isBookingVisibleToInstructorNow(b, visibilityPolicy, now))
      : rawBookings;

    // Pass 58A: an all-day staff activity (RDO/timeoff) has NO startAt — it carries only a canonical
    // `serviceDate`. Place it in the window/day/week using its serviceDate day-start (JST) so the
    // instructor's own RDO shows on My Schedule, WITHOUT fabricating a 09:00 startAt. Client bookings
    // (startAt present) are unaffected. Sort keys date-only entries to the start of their day.
    const dayMsOf = (b) => {
      const t = toDate(b.startAt)?.getTime();
      if (t != null) return t;
      const d = b.serviceDate ? wallClockToInstant(b.serviceDate, '00:00', tz) : null;
      return d ? d.getTime() : null;
    };
    const dayKeyOf = (b) => (b.startAt ? zonedDateKey(b.startAt, tz) : String(b.serviceDate || ''));

    const inWindow = published.filter((b) => {
      const t = dayMsOf(b);
      return t != null && t >= windowStart && t < windowEnd;
    });

    const sorted = [...inWindow].sort((a, b) => (dayMsOf(a) || 0) - (dayMsOf(b) || 0));

    // Pass 83B.3 §11 — the FULL published personal schedule (scope-matched + visibility-filtered) with NO
    // operational date-window slice. My Schedule keeps using the near-term `all`; My Calendar uses this
    // `publishedAll` so a booking later in the published month (beyond the 14-day operational window) still
    // appears on the month view — WITHOUT a second Firestore listener and WITHOUT leaking hidden-future
    // bookings (the visibility policy has already been applied to `published`).
    const publishedAll = [...published]
      .filter((b) => dayMsOf(b) != null)
      .sort((a, b) => (dayMsOf(a) || 0) - (dayMsOf(b) || 0));

    const todayKey = zonedDateKey(now, tz);
    const tomorrowKey = zonedDateKey(zonedShiftDays(now, 1, tz), tz);

    const byDay = {};
    for (const b of sorted) {
      const k = dayKeyOf(b);
      if (!k) continue;
      if (!byDay[k]) byDay[k] = [];
      byDay[k].push(b);
    }

    const activeOnly = (list) => list.filter((b) => b.status !== 'canceled');

    return {
      all: sorted,
      publishedAll,
      byDay,
      today: byDay[todayKey] || [],
      tomorrow: byDay[tomorrowKey] || [],
      todayKey,
      tomorrowKey,
      week: sorted.filter((b) => {
        const t = dayMsOf(b);
        return t != null && t >= todayStart && t < weekEnd;
      }),
      activeCount: activeOnly(sorted).length,
      // Pass 57A — honest UX: how far the schedule is currently published (null = full season), and
      // whether a restrictive horizon is in force (drives "No work in your published window" + calendar
      // navigation limits). Never reveals counts/details beyond the horizon.
      publishedThrough: visibilityPolicy ? publishedThroughDate(visibilityPolicy, now) : null,
      visibilityApplicable: !!(visibilityPolicy && visibilityPolicy.applicable),
    };
  }, [snap, matched, daysBefore, daysAfter, visibilityPolicy, timeZone]);

  // §12/§14 — an Auth identity is present but no organization scope could be resolved → the surface should
  // show an honest "organization context" state, NOT a false "No work scheduled" (the query never ran).
  const scopeMissing = !!instructorId && !organizationId;
  return { loading, error, lastUpdatedAt, scopeMissing, ...derived };
}
